import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  Plus, Edit2, Trash2, Search, MapPin, Clock, User, 
  Mail, Phone, Globe, Building2, Loader2, CheckCircle,
  X, Calendar, Database
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'All Levels'];

export function AdminClassesTab() {
  const [classes, setClasses] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    day_of_week: 'Monday',
    start_time: '19:00',
    end_time: '21:00',
    venue_name: '',
    address: '',
    city: '',
    postcode: '',
    level: 'All Levels',
    price: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    club_id: '',
    instructor_id: '',
    notes: '',
    is_active: true
  });

  useEffect(() => {
    fetchClasses();
    fetchClubs();
    fetchInstructors();
  }, []);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/classes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClasses(data);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clubs`);
      if (res.ok) {
        const data = await res.json();
        setClubs(data);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  const fetchInstructors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/instructors`);
      if (res.ok) {
        const data = await res.json();
        setInstructors(data);
      }
    } catch (error) {
      console.error('Error fetching instructors:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const url = editingClass
        ? `${API_URL}/api/admin/classes/${editingClass.class_id}`
        : `${API_URL}/api/admin/classes`;
      
      const res = await fetch(url, {
        method: editingClass ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingClass ? 'Class updated' : 'Class created');
        setShowForm(false);
        setEditingClass(null);
        resetForm();
        fetchClasses();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save class');
      }
    } catch (error) {
      console.error('Error saving class:', error);
      toast.error('Failed to save class');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/classes/${classId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Class deleted');
        fetchClasses();
      } else {
        toast.error('Failed to delete class');
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error('Failed to delete class');
    }
  };

  const handleSeedClasses = async () => {
    if (!window.confirm('This will add sample UK line dance classes to the database. Continue?')) return;

    setSeeding(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/classes/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchClasses();
      } else {
        toast.error(data.message || 'Failed to seed classes');
      }
    } catch (error) {
      console.error('Error seeding classes:', error);
      toast.error('Failed to seed classes');
    } finally {
      setSeeding(false);
    }
  };

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setFormData({
      name: cls.name || '',
      description: cls.description || '',
      day_of_week: cls.day_of_week || 'Monday',
      start_time: cls.start_time || '19:00',
      end_time: cls.end_time || '21:00',
      venue_name: cls.venue_name || '',
      address: cls.address || '',
      city: cls.city || '',
      postcode: cls.postcode || '',
      level: cls.level || 'All Levels',
      price: cls.price || '',
      contact_name: cls.contact_name || '',
      contact_email: cls.contact_email || '',
      contact_phone: cls.contact_phone || '',
      website: cls.website || '',
      club_id: cls.club_id || '',
      instructor_id: cls.instructor_id || '',
      notes: cls.notes || '',
      is_active: cls.is_active !== false
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      day_of_week: 'Monday',
      start_time: '19:00',
      end_time: '21:00',
      venue_name: '',
      address: '',
      city: '',
      postcode: '',
      level: 'All Levels',
      price: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      club_id: '',
      instructor_id: '',
      notes: '',
      is_active: true
    });
  };

  const filteredClasses = classes.filter(cls =>
    cls.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.venue_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Line Dance Classes</h2>
          <p className="text-slate-600 text-sm">{classes.length} classes in database</p>
        </div>
        <div className="flex gap-2">
          {classes.length === 0 && (
            <Button
              onClick={handleSeedClasses}
              disabled={seeding}
              variant="outline"
              className="border-violet-200 text-violet-700 hover:bg-violet-50"
            >
              {seeding ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              Seed Sample Classes
            </Button>
          )}
          <Button
            onClick={() => {
              setEditingClass(null);
              resetForm();
              setShowForm(true);
            }}
            className="bg-violet-600 hover:bg-violet-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Class
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Search by name, city, or venue..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingClass ? 'Edit Class' : 'Add New Class'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Class Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="e.g., Beginners Line Dance"
                    />
                  </div>

                  <div>
                    <Label>Day of Week *</Label>
                    <select
                      value={formData.day_of_week}
                      onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-slate-300"
                      required
                    >
                      {DAYS_OF_WEEK.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>Level</Label>
                    <select
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-slate-300"
                    >
                      {LEVELS.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>Start Time *</Label>
                    <Input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    />
                  </div>
                </div>

                {/* Venue Info */}
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Venue Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Venue Name *</Label>
                      <Input
                        value={formData.venue_name}
                        onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
                        required
                        placeholder="e.g., Village Hall"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label>Address</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Street address"
                      />
                    </div>

                    <div>
                      <Label>City *</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                        placeholder="e.g., Birmingham"
                      />
                    </div>

                    <div>
                      <Label>Postcode</Label>
                      <Input
                        value={formData.postcode}
                        onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                        placeholder="e.g., B1 1AA"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact & Price */}
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Contact & Pricing</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Price</Label>
                      <Input
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="e.g., £5 per session"
                      />
                    </div>

                    <div>
                      <Label>Contact Name</Label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label>Contact Email</Label>
                      <Input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label>Contact Phone</Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label>Website</Label>
                      <Input
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        placeholder="https://"
                      />
                    </div>
                  </div>
                </div>

                {/* Links */}
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Link to Existing Records (Optional)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Link to Club</Label>
                      <select
                        value={formData.club_id}
                        onChange={(e) => setFormData({ ...formData, club_id: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-300"
                      >
                        <option value="">No club link</option>
                        {clubs.map(club => (
                          <option key={club.club_id} value={club.club_id}>{club.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Link to Instructor</Label>
                      <select
                        value={formData.instructor_id}
                        onChange={(e) => setFormData({ ...formData, instructor_id: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-300"
                      >
                        <option value="">No instructor link</option>
                        {instructors.map(inst => (
                          <option key={inst.instructor_id} value={inst.instructor_id}>{inst.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="border-t pt-4">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Describe the class..."
                  />
                </div>

                {/* Notes & Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Internal Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                      placeholder="Admin notes (not shown publicly)"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-6">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="is_active" className="cursor-pointer">Active (visible on site)</Label>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    {editingClass ? 'Update Class' : 'Create Class'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Classes List */}
      {filteredClasses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">
              {searchTerm ? 'No classes match your search' : 'No classes yet. Add your first class or seed sample data.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredClasses.map(cls => (
            <Card key={cls.class_id} className={!cls.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900">{cls.name}</h3>
                          <Badge className={getLevelColor(cls.level)}>{cls.level}</Badge>
                          {!cls.is_active && <Badge variant="secondary">Inactive</Badge>}
                        </div>
                        
                        <div className="mt-2 space-y-1 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-violet-500" />
                            <span>{cls.day_of_week}</span>
                            <Clock className="w-4 h-4 text-violet-500 ml-2" />
                            <span>{cls.start_time}{cls.end_time ? ` - ${cls.end_time}` : ''}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-violet-500" />
                            <span>{cls.venue_name}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-violet-500" />
                            <span>{cls.city}{cls.postcode ? `, ${cls.postcode}` : ''}</span>
                          </div>

                          {cls.price && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-green-600">{cls.price}</span>
                            </div>
                          )}

                          {(cls.club_name || cls.instructor_name) && (
                            <div className="flex items-center gap-4 mt-2 pt-2 border-t">
                              {cls.club_name && (
                                <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded">
                                  Club: {cls.club_name}
                                </span>
                              )}
                              {cls.instructor_name && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                  Instructor: {cls.instructor_name}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {cls.description && (
                          <p className="mt-2 text-sm text-slate-500 line-clamp-2">{cls.description}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(cls)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(cls.class_id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
